"use client";

/**
 * Shared Sonner toaster (shadcn-aligned).
 * @see https://ui.shadcn.com/docs/components/radix/sonner
 *
 * Placement lock (keep when changing styles):
 * - position: top-right
 * - under fixed header: `!top-[calc(4rem+0.5rem)]`
 * - over right sidebar gutter: `!right-2`
 * - width constrained to right rail: `max-w-[min(24rem,calc(100vw-1rem))]`
 */
import { toast as sonnerToast } from "sonner";
import { Toaster as Sonner } from "sonner";

type ToasterProps = React.ComponentProps<typeof Sonner>;

const TOASTER_PLACEMENT_CLASSNAME =
	"toaster group !top-[calc(4rem+0.5rem)] !right-2 max-w-[min(24rem,calc(100vw-1rem))] !z-[9999]";

const Toaster = ({ ...props }: ToasterProps) => {
	return (
		<Sonner
			className={TOASTER_PLACEMENT_CLASSNAME}
			toastOptions={{
				classNames: {
					toast: "group toast !rounded-lg !font-sans group-[.toaster]:bg-card group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-md !max-w-[min(24rem,calc(100vw-1rem))]",
					description: "group-[.toast]:text-muted-foreground",
					actionButton:
						"group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
					cancelButton:
						"group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
					success: "!text-success",
					error: "!text-destructive",
				},
				duration: 5000,
			}}
			{...props}
		/>
	);
};

/** Re-export sonner `toast` API from the shared UI module. */
const toast: typeof sonnerToast = sonnerToast;

export interface ToastProposedActionParams {
	id: string;
	title: string;
	description?: string;
	primaryLabel?: string;
	secondaryLabel?: string;
	onPrimary?: () => void | Promise<void>;
	onSecondary?: () => void | Promise<void>;
}

/**
 * Sticky action toast for proposed-action / live parity.
 * Does not auto-dismiss (`duration: Infinity`).
 */
export function toastProposedAction(
	params: ToastProposedActionParams,
): string | number {
	return toast(params.title, {
		id: params.id,
		description: params.description,
		duration: Number.POSITIVE_INFINITY,
		closeButton: true,
		action: params.onPrimary
			? {
					label: params.primaryLabel ?? "Proceed",
					onClick: () => {
						void params.onPrimary?.();
					},
				}
			: undefined,
		cancel: params.onSecondary
			? {
					label: params.secondaryLabel ?? "Skip",
					onClick: () => {
						void params.onSecondary?.();
					},
				}
			: undefined,
	});
}

export interface ToastLiveProgressParams {
	id?: string;
	title: string;
	description?: string;
	/** Default 4000ms; progress toasts must auto-dismiss. */
	durationMs?: number;
}

/**
 * Ephemeral live/tool progress toast (auto-dismisses).
 */
export function toastLiveProgress(
	params: ToastLiveProgressParams,
): string | number {
	return toast(params.title, {
		id: params.id,
		description: params.description,
		duration: params.durationMs ?? 4000,
	});
}

export function dismissToast(id?: string | number): void {
	toast.dismiss(id);
}

export { Toaster, toast, TOASTER_PLACEMENT_CLASSNAME };
